import { computed, DestroyRef, effect, Injectable, signal } from '@angular/core';
import { IOnboardingUserSettingsSaveModel } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion';
import { isDefined } from '@fi-shared/utils';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import {
    IOnboardingActive,
    IOnboardingActiveData,
    IOnboardingParameter,
} from '@fi-app/fi-onboarding/fi-onboarding-builder/fi-onboarding';
import { FiSignalRHelperService } from '@fi-shared/fi-signal-r/fi-signal-r-helper.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { FiOnboardingStatusType } from '@fi-app/fi-onboarding/fi-onboarding-enums';
import { BehaviorSubject } from 'rxjs';

@Injectable()
export class FiOnboardingActiveDataService {
    constructor(
        private fiBackend: FiBackendService,
        private fiSignalRHelperService: FiSignalRHelperService,
    ) {}

    private _onboardingActiveData = signal<IOnboardingActiveData>({
        ActiveOnboardings: [],
        OnboardingAssignees: [],
        LightWeightObjectsForLocalSearch: undefined,
    });
    readonly onboardingActiveData = this._onboardingActiveData.asReadonly();

    todoOnboardings = computed<IOnboardingActive[]>(() => {
        const todoOnboardings = this._onboardingActiveData().ActiveOnboardings.filter(
            (item) =>
                item.Status === FiOnboardingStatusType.InProgress || item.Status === FiOnboardingStatusType.NotStarted,
        );
        return todoOnboardings;
    });

    todoOnboardingsCount = computed<number>(() => {
        return this.todoOnboardings().length;
    });
    todoOnboardingsCount$ = new BehaviorSubject<number>(0);

    todoOnboardingsCountEffect = effect(() => {
        const todoOnboardingsCount = this.todoOnboardingsCount();
        this.todoOnboardingsCount$.next(todoOnboardingsCount);
    });

    init(destroyRef: DestroyRef) {
        this.fiSignalRHelperService.onboardingActiveData$
            .pipe(
                takeUntilDestroyed(destroyRef),
                filter((onboardingHomePageData) => isDefined(onboardingHomePageData)),
            )
            .subscribe((onboardingHomePageData) => {
                this.setOnboardingActiveData(onboardingHomePageData);
            });
    }

    reloadOnboardingActiveData(onboardingIds: number[] = null): void {
        // not optimal todo
        this.getOnboardingActiveData({
            Ids: onboardingIds,
        }).then((result) => this.setOnboardingActiveData(result));
    }

    private setOnboardingActiveData(onboardingActivityPageData: IOnboardingActiveData): void {
        this._onboardingActiveData.set(onboardingActivityPageData);
        this.fiSignalRHelperService.setActiveOnboardingsCount(this._onboardingActiveData());
    }

    private updateActiveOnboardingsInternal(updateFn: (activeOnboardingsToUpdate: IOnboardingActive[]) => void): void {
        this._onboardingActiveData.update((current) => {
            const activeOnboardings = current.ActiveOnboardings.slice(0);
            updateFn(activeOnboardings);
            // now activeOnboardings contain updated info, user settings, completed count etc
            return {
                ...current,
                ActiveOnboardings: activeOnboardings,
            };
        });
        this.fiSignalRHelperService.setActiveOnboardingsCount(this._onboardingActiveData());
    }

    public updateOnboardingActivityDataWithUserSettings(newUserSettings: IOnboardingUserSettingsSaveModel): void {
        this.updateActiveOnboardingsInternal((activeOnboardingsToUpdate) => {
            newUserSettings.OnboardingIds.forEach((onboardingId) => {
                const activeOnboarding = activeOnboardingsToUpdate.find((onboarding) => onboarding.Id === onboardingId);
                if (isDefined(newUserSettings.IsAtSideWidgets)) {
                    activeOnboarding.IsAtSideWidgets = newUserSettings.IsAtSideWidgets;
                }
                if (isDefined(newUserSettings.IsHiddenFromHomePage)) {
                    activeOnboarding.IsHiddenFromHomePage = newUserSettings.IsHiddenFromHomePage;
                }
            });
        });
    }

    /*updateCompleteStepsCount(onboardingId: number, userCompletion: IOnboardingUserCompletion) {
        this.updateActiveOnboardingsInternal((activeOnboardingsToUpdate) => {});
    }*/

    //#region fetch data from backend
    private getOnboardingActiveData(parameter: IOnboardingParameter) {
        return this.fiBackend.fetchDataPost<IOnboardingActiveData>(
            `Onboarding/OnboardingActive/GetOnboardingActiveData`,
            parameter,
        );
    }
    //#endregion
}
